import React from 'react';
import { ActivityLogs } from '@fabric-ui/icons';
import { QueryKey } from '@fabric-ui/utils';
import { gql } from 'graphql-request';
import { useGqlQuery } from '../../API/useAvQuery';
import { useNavigate } from '../../utils/AvRouter';
import { AuditEntry } from './types';

interface AuditLogResponse {
  content: AuditEntry[];
  totalPages: number;
  totalElements: number;
}

interface SelectedAuditLogs {
  totalElements: number;
  rowsFromQuery: AuditEntry[];
}

const GET_LOGS_LIST = gql`
  query getAuditEntriesById($entity: AuditEntityType!, $entityId: String!, $pageable: Pageable) {
    getAuditEntriesById(entity: $entity, entityId: $entityId, pageable: $pageable) {
      content {
        entryId
        entityId
        accountId
        actorId
        operation
        oldRowData
        newRowData
        transactionId
        dmlTimestamp
        type
      }
      totalPages
      totalElements
    }
  }
`;

export const useAuditLogs = ({ id, entityType, pageVars, onError }) =>
  useGqlQuery<AuditLogResponse, SelectedAuditLogs>({
    queryKey: [QueryKey.GetAuditLogs, id, pageVars.pageNumber],
    query: GET_LOGS_LIST,
    variables: { entity: entityType, entityId: id, pageable: pageVars },
    onError,
    select: data => ({
      totalElements: data.totalElements,
      rowsFromQuery: data.content,
    }),
    enabled: !!id,
    refetchInterval: 30000,
    gcTime: 0,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

const GET_ALL_LOGS_LIST = gql`
  query getAuditEntriesByAccountId($entity: AuditEntityType!, $pageable: Pageable) {
    getAuditEntriesByAccountId(entity: $entity, pageable: $pageable) {
      content {
        entryId
        entityId
        accountId
        actorId
        operation
        oldRowData
        newRowData
        transactionId
        dmlTimestamp
        type
      }
      totalPages
      totalElements
    }
  }
`;
export const useAuditLogsByAccount = ({ entityType, pageVars, onError, enabled = true }) =>
  useGqlQuery<AuditLogResponse, SelectedAuditLogs>({
    queryKey: [QueryKey.GetAuditLogsAll, entityType, pageVars.pageNumber],
    query: GET_ALL_LOGS_LIST,
    variables: { entity: entityType, pageable: pageVars },
    onError,
    select: data => ({
      totalElements: data.totalElements,
      rowsFromQuery: data.content,
    }),
    enabled,
    refetchInterval: 30000,
    gcTime: 0,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

export const useAuditLogsAll = ({ id, entity, pageVars, select }) =>
  useGqlQuery<AuditLogResponse, AuditEntry[]>({
    queryKey: [QueryKey.GetAuditLogsAll, id],
    query: GET_ALL_LOGS_LIST,
    variables: {
      entity,
      pageable: pageVars,
    },
    select: data => select(data?.content || []),
    enabled: !!id,
    refetchInterval: 30000,
    gcTime: 0,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

export const useAuditNavigation = ({
  id = '',
  hasIcon = false,
  search,
  ignoreBlocker = false,
}: {
  id?: string;
  hasIcon?: boolean;
  search?: Record<string, any>;
  ignoreBlocker?: boolean;
} = {}) => {
  const navigate = useNavigate();
  return {
    menuOptions: [
      {
        icon: hasIcon ? <ActivityLogs /> : undefined,
        title: 'Audit Logs',
        onClick: () => navigate({ to: `${window.location.pathname}/audit/${id}`, search, ignoreBlocker }),
      },
    ],
  };
};

export const buildPagination = ({ page, pageSize }: { page: number; pageSize?: number }) => ({
  pageNumber: +page || 0,
  pageSize: pageSize || 20,
});
